"use client";

import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";

import { zeroPad } from "@/lib/utils";
import Envelope from "@/components/icons/envelope-outline.svg";
import { RetrievedProductCategory, RetrievedService } from "@/lib/sanity/responseTypes";

interface ServiceRequestFormProps {
  service: RetrievedService;
  productCategories: RetrievedProductCategory[];
  submitPath: string;
  extraFields?: {
    type: "string" | "dropdown"
    placeholder: string;
    possibleValues?: { value: string, label?: string, notAnOption?: boolean }[];
    defaultValue?: string;
  }[];
  close: () => void;
  setSuccessToast: (m: string) => void;
  setErrorToast: (m: string) => void;
}

export default function ServiceRequestForm(props: ServiceRequestFormProps) {
  const locale = useLocale();
  const t = useTranslations("Services");

  const { setSuccessToast, setErrorToast, close, service, productCategories, submitPath } = props;

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [company, setCompany] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [targetDate, setTargetDate] = useState<string>("");
  const [numberOfParticipants, setNumberOfParticipants] = useState<number | string>("");
  const [message, setMessage] = useState<string>("");
  const [phoneNumberValidity, setPhoneNumberValidity] = useState<boolean>(false);
  const [emailValidity, setEmailValidity] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(false);

  const minDate = new Date(Date.now() + 24 * 3600 * 1000);
  const minDateYyyyMmDd = `${minDate.getFullYear()}-${zeroPad(minDate.getMonth() + 1, 2)}-${zeroPad(minDate.getDate(), 2)}`;

  function canBeSubmitted() {
    return firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      company.trim().length > 0 &&
      email.trim().length > 0 &&
      phoneNumber.trim().length > 0 &&
      selectedCategory.trim().length > 0 &&
      message.trim().length > 0 &&
      emailValidity &&
      phoneNumberValidity;
  }

  function clearForm() {
    setFirstName("");
    setLastName("");
    setCompany("");
    setPhoneNumber("")
    setEmail("");
    setSelectedCategory("");
    setTargetDate("");
    setNumberOfParticipants(0);
    setMessage("");
  }

  async function onSubmitClick() {
    if (isLoading) return;
    setIsLoading(true);
    try {
      let res = await fetch(submitPath, {
        method: "POST", body: JSON.stringify({
          firstName,
          lastName,
          company,
          email,
          phoneNumber,
          category: selectedCategory,
          preferredDate: targetDate.split('-').toReversed().join('.'),
          numberOfParticipants,
          message,
          service: service,
        })
      });

      let resBody = await res.json();

      if (res.ok) {
        setSuccessToast(t("req_submitted"));
        clearForm();
        close();
      } else {
        console.error(`Received response: ${JSON.stringify(resBody)}`);
        setErrorToast(t('error'));
      }
    } catch (err) {
      console.error(`Error while sending contact form: ${err}`);
      setErrorToast(JSON.stringify(err));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex flex-col items-center gap-2.5 w-full">
      <div className="flex flex-col gap-y-2.5 md:flex-row md:gap-x-2.5 w-full">
        <input className="input md:flex-1 grow w-full" type="text" required placeholder={t('field_firstname')} onChange={e => setFirstName(e.target.value)} value={firstName} />
        <input className="input md:flex-1 grow w-full" type="text" required placeholder={t('field_lastname')} onChange={e => setLastName(e.target.value)} value={lastName} />
      </div>
      <div className="flex flex-col gap-y-2.5 md:flex-row md:gap-x-2.5 w-full">
        <input className="input md:flex-1 grow w-full validator" type="tel" pattern="(?:(?:(?:00|\+)41)|0)[1-9](?:[ -()]*\d){8}" placeholder={t('field_phonenumber')} onChange={e => { setPhoneNumber(e.target.value); setPhoneNumberValidity(!e.target.validity.typeMismatch && !e.target.validity.patternMismatch) }} value={phoneNumber} />
        <input className="input md:flex-1 grow w-full validator" type="email" required placeholder={t('field_email')} onChange={e => { setEmail(e.target.value); setEmailValidity(!e.target.validity.typeMismatch); }} value={email} />
      </div>
      <div className="flex flex-col gap-y-2.5 md:flex-row md:gap-x-2.5 w-full">
        <input className="input md:flex-1 grow w-full" type="text" required placeholder={t('field_company')} onChange={e => setCompany(e.target.value)} value={company} />
        <select className="select md:flex-1 grow w-full pr-3" onChange={e => setSelectedCategory(e.target.value)} defaultValue="regarding_category">
          <option disabled={true} value="regarding_category">{t('regarding_category')}</option>
          {productCategories.map((c, i) => {
            return (
              <option key={`product-category-${i}`} value={c.name}>{c.title[locale]}</option>
            )
          })}
        </select>
      </div>
      <div className="flex flex-col gap-y-2.5 md:flex-row md:gap-x-2.5 w-full">
        <input className="input md:flex-1 grow w-full validator" type="date" min={minDateYyyyMmDd} placeholder={t('field_preferred_date')} onChange={e => setTargetDate(e.target.value)} />
        <input className="input md:flex-1 grow w-full validator" type="number" placeholder={t('field_number_participants')} min="1" step="1" onChange={e => { setNumberOfParticipants(parseInt(e.target.value)) }} value={numberOfParticipants} />
      </div>
      <div className="flex flex-col md:flex-row w-full">
        <textarea className="textarea w-full" required placeholder={t('field_message')} onChange={e => setMessage(e.target.value)} value={message}></textarea>
      </div>
      <div className="p-2.5">
        <button className={"btn btn-secondary text-white rounded-full"} onClick={onSubmitClick} disabled={!canBeSubmitted() || isLoading}>
          <Envelope height={14} width={14} className="stroke-white" />
          {isLoading ? (
            <span className="loading loading-spinner loading-sm">{" "}</span>
          ) : (
            t("send-request")
          )}
        </button>
      </div>
    </div >
  )
}
